import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError, describeError } from "../../api/client";
import type { AdminAudienceFilterSelection, AdminCampaignDraft } from "../../api/types";
import { RequireRole } from "../../auth/RequireRole";
import { useAccountRoles } from "../../auth/useAccountRoles";
import { DeliveryProgressBar } from "../../components/DeliveryProgressBar";
import { completionLine, sendPaceCards } from "./sendPace";
import { DetailGroup } from "../../components/DetailGroup";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { renderValidatedCount } from "../../components/MetricCell";
import { NotFoundCard } from "../../components/NotFoundCard";
import { StatusPill } from "../../components/StatusPill";
import { useToast } from "../../components/ToastProvider";
import { datetimeLocalValueToIso, formatDateTime, isoToDatetimeLocalValue } from "../../formatting/date";
import { formatCount, isValidCount } from "../../formatting/number";
import { isTerminalLifecycleState, resolveLifecycleStatus } from "../../formatting/status";
import { invalidateExact } from "../../state/queryClient";
import { buildAllowedSearchParams } from "../../state/routeState";
import { useMutation } from "../../state/useMutation";
import { useQuery } from "../../state/useQuery";
import { getCampaignTelemetry } from "../telemetry/api";
import { ShowSelector } from "../contacts/ShowSelector";
import {
  cancelCampaign,
  dispatchCampaign,
  getAudienceFilter,
  getCampaign,
  getCampaignAudiencePreview,
  getCampaignMonitoring,
  saveAudienceFilter,
  scheduleCampaign,
  updateCampaign,
} from "./api";
import {
  audienceSummaryLabel,
  formatSentHeadline,
  formatDeliveredPercent,
  formatEmpresas,
  formatOrDash,
  formatPaisCargoCategoria,
  isEmptyAudienceSelection,
  lifecycleControls,
  metricValue,
  pendingCount,
  resolveEmpresas,
  statusLineFor,
  waitTextFor,
  waitTitleFor,
} from "./campaignDetailDerived";
import { describeConsequentialActionError } from "./consequentialActions";
import { EmpresaChips, EmpresaSelector } from "./EmpresaSelector";
import { CAMPAIGN_LIST_ALLOWED_KEYS } from "./filters";
import { formatActor } from "../../formatting/actor";
import { validateScheduleDate } from "./scheduleValidation";
import { TemplateSelector } from "./TemplateSelector";

type ActiveModal = "schedule" | "dispatch" | "cancel" | "edit" | null;

// async-campaign-dispatch (Option A, native review correction PR #311): the
// detail query itself has no background poll (unlike monitoring/telemetry
// below), so right after a 202 dispatch acknowledgement a single
// refetchDetail() can still lose the race against the background
// CampaignDispatchQueueFunction consumer flipping 'programada' -> 'enviando'.
// Same bounded-poll shape the pre-merge CampaignSummaryTab.tsx used.
const DISPATCH_POLL_INTERVAL_MS = 1500;
const DISPATCH_POLL_MAX_ATTEMPTS = 5;

// panel-feedback-round2 T1: the detail query also polls while the campaign is
// in flight, so the badge/status line follow the backend without a reload.
const DETAIL_POLL_INTERVAL_MS = 10_000;

// pace-card-live-update: re-render cadence for the live elapsed time while sending.
const PACE_TICK_INTERVAL_MS = 1000;

/**
 * Whether the detail query should keep polling for this estado. `borrador`
 * only changes through a user action (mutations invalidate the query), and
 * `parcial` is a closing outcome written by the finalizer (never re-opened),
 * so neither needs a background poll; terminal states never change again.
 */
function shouldPollDetail(estado: string): boolean {
  return estado !== "borrador" && estado !== "parcial" && !isTerminalLifecycleState(estado);
}

function audienceCriteriaKey(filter: AdminAudienceFilterSelection): string {
  return JSON.stringify([
    filter.showId,
    filter.paisEmpresa,
    [...(filter.empresas ?? [])].sort(),
    filter.cargo,
    filter.categoria,
  ]);
}

/** A small label/value box used by the 4 "Progreso del envío" mini-cards. */
function MiniStat({
  label,
  value,
  hint,
  critical,
  tone,
}: {
  label: string;
  // Prototype: each mini-card label carries an 8x8 square dot in its own color.
  tone: "delivered" | "error" | "pending" | "excluded" | "duration" | "rate";
  // Codex finding (PR #384 round 5): a mini-stat whose count is invalid
  // renders `renderValidatedCount`'s own critical span (shared with
  // MetricCell/OverviewScreen/TelemetryScreen), not a plain formatted
  // string.
  value: ReactNode;
  hint: string;
  critical?: boolean;
}) {
  return (
    <div
      className={[
        "v2-campaign-progress__stat",
        critical ? "v2-campaign-progress__stat--critical" : "",
        tone === "delivered" ? "v2-campaign-progress__stat--delivered" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="v2-campaign-progress__stat-label">
        <span className={`v2-campaign-progress__stat-dot v2-campaign-progress__stat-dot--${tone}`} aria-hidden="true" />
        {label}
      </span>
      <span className="v2-campaign-progress__stat-value v2-u-tabular-nums">{value}</span>
      <span className="v2-campaign-progress__stat-hint">{hint}</span>
    </div>
  );
}

/**
 * `/campanas/:campaignId` — REDISENO-PANEL.md §5.3: the former 3-tab shell
 * (`CampaignDetailScreen` + `CampaignSummaryTab`/`CampaignMonitoringTab`/
 * `CampaignTelemetryTab`) is now ONE page. `/campanas/:campaignId/monitoreo`
 * and `/campanas/:campaignId/telemetria` redirect (`replace`) here —
 * see `app/routes.tsx`.
 *
 * Nonexistent and foreign-tenant campaign IDs render the IDENTICAL local
 * not-found state (non-disclosing tenant contract: the backend repository
 * scopes `GetByIdAsync` by `client_id`).
 */
export function CampaignDetailScreen() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const id = campaignId as string;
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const roles = useAccountRoles();
  const confirmedConsulta = roles.includes("Consulta") && !roles.includes("Operador");

  const contextSuffix = useMemo(() => {
    const allowed = buildAllowedSearchParams(searchParams.toString(), CAMPAIGN_LIST_ALLOWED_KEYS);
    const asString = allowed.toString();
    return asString ? `?${asString}` : "";
  }, [searchParams]);

  // `pollIntervalMs` is derived from the latest data: undefined (no poll) for
  // borrador/terminal, and it re-arms automatically when a mutation moves the
  // campaign into an in-flight state.
  const detailEstadoRef = useRef<string | undefined>(undefined);
  const { data, error, loading, stale, refetch: refetchDetail } = useQuery({
    key: ["campaign", id],
    queryFn: () => getCampaign(id),
    pollIntervalMs:
      detailEstadoRef.current !== undefined && shouldPollDetail(detailEstadoRef.current)
        ? DETAIL_POLL_INTERVAL_MS
        : undefined,
    isTerminal: (campaign) => (campaign ? !shouldPollDetail(campaign.estado) : false),
  });
  detailEstadoRef.current = data?.estado;

  const isNotFound = error instanceof ApiError && error.kind === "notFound";

  // Same eviction primitive as the pre-merge CampaignDetailScreen.tsx: a
  // not-found branch alone only protects the CURRENT render — without
  // evicting the shared cache entry, navigating away and back to the same
  // URL would rehydrate the deleted/moved campaign from the still-cached,
  // now-stale entry with no error yet.
  const evictedForCampaignIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isNotFound && id && evictedForCampaignIdRef.current !== id) {
      evictedForCampaignIdRef.current = id;
      invalidateExact([["campaign", id]]);
    }
  }, [isNotFound, id]);

  // Codex PR #384 follow-up finding 6: see `MoreMenu`'s own `triggerRef` prop
  // and `Modal`'s own `restoreFocusTo` prop docstrings. Declared above the
  // early loading/not-found/error returns below -- hooks must run in the
  // same order every render.
  const moreMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const isPending = loading || (!data && error === undefined);

  const monitoringQuery = useQuery({
    key: ["campaign-monitoring", id],
    queryFn: () => getCampaignMonitoring(id),
    pollIntervalMs: 10_000,
    isTerminal: (monitoring) => (monitoring ? isTerminalLifecycleState(monitoring.estado) : false),
  });

  const telemetryQuery = useQuery({
    key: ["campaign-telemetry", id],
    queryFn: () => getCampaignTelemetry(id),
    pollIntervalMs: 10_000,
    // Codex finding 5 (PR #384): the detail query (`data`) above has no poll
    // of its own, so `data.estado` can lag well behind reality once a
    // background CampaignDispatchQueueFunction consumer moves the campaign
    // through 'enviando' -> a terminal state — keying this off the
    // ALREADY-POLLED monitoring query instead means telemetry polling stops
    // as soon as monitoring itself observes the terminal state.
    isTerminal: () =>
      monitoringQuery.data ? isTerminalLifecycleState(monitoringQuery.data.estado) : false,
  });

  // The monitoring query is polled independently and may observe the new
  // estado first: refresh the detail right away (once per distinct
  // monitoring -> detail pair, so a persistent disagreement cannot loop).
  const monitoringEstado = monitoringQuery.data?.estado;
  const detailEstado = data?.estado;
  const monitoringSyncRef = useRef<string | null>(null);
  useEffect(() => {
    if (!monitoringEstado || !detailEstado || monitoringEstado === detailEstado) return;
    const pair = `${monitoringEstado}->${detailEstado}`;
    if (monitoringSyncRef.current === pair) return;
    monitoringSyncRef.current = pair;
    invalidateExact([["campaign", id]]);
  }, [monitoringEstado, detailEstado, id]);

  // pace-card-live-update: "Tiempo de envío"/"Ritmo promedio" read the clock while rendering, so
  // between 10 s polls nothing re-rendered them. A cheap 1 s tick (only while sending, cleaned
  // up on unmount/terminal) keeps the elapsed time and pace moving on their own.
  const sendingNow = monitoringEstado === "enviando";
  const [, setPaceTick] = useState(0);
  useEffect(() => {
    if (!sendingNow) return undefined;
    const timer = setInterval(() => setPaceTick((tick) => tick + 1), PACE_TICK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [sendingNow]);

  // The monitoring/telemetry polls stop the moment they observe a terminal estado, and the detail
  // poll can observe it first: when the campaign goes from in flight to terminal, refresh both
  // once more so the final enviados/ultimoEnvioEn (and the final pace) are loaded and then frozen.
  const monitoringTerminal = monitoringEstado !== undefined && isTerminalLifecycleState(monitoringEstado);
  const detailTerminal = detailEstado !== undefined && isTerminalLifecycleState(detailEstado);
  const campaignTerminal = monitoringTerminal || detailTerminal;
  const sawInFlightRef = useRef(false);
  const { refetch: refetchMonitoring } = monitoringQuery;
  const { refetch: refetchTelemetry } = telemetryQuery;
  useEffect(() => {
    if (monitoringEstado === undefined) return;
    if (!campaignTerminal) {
      sawInFlightRef.current = true;
      return;
    }
    if (!sawInFlightRef.current) return;
    sawInFlightRef.current = false;
    void refetchMonitoring();
    void refetchTelemetry();
  }, [campaignTerminal, monitoringEstado, refetchMonitoring, refetchTelemetry]);

  const audienceFilterQuery = useQuery({
    key: ["campaign-audience-filter", id],
    queryFn: () => getAudienceFilter(id),
  });
  const audienceFilterNotFound =
    audienceFilterQuery.error instanceof ApiError && audienceFilterQuery.error.kind === "notFound";
  const audienceFilterLoadError = audienceFilterQuery.error !== undefined && !audienceFilterNotFound;
  // Codex finding (PR #384 round 5, P1): when the filter initializes from
  // cache and its CONFIRMING request fails, `useQuery` retains `data`,
  // clears `refreshing` (the request is no longer in flight), and marks the
  // result `stale` — without checking `stale`/`audienceFilterLoadError`
  // here, this predicate flipped back to "settled" and re-enabled Edit/Save
  // even though the server value was never verified, letting an operator
  // save the cached form and overwrite a newer audience restriction.
  // `stale` alone (independent of `error`) also must block: a failure
  // BROADCAST from another subscriber of the same query key only flips
  // `phase`/`stale`, never touches `error` (see `useQuery.ts`'s
  // `subscribeInvalidate` "failure" branch).
  //
  // Codex PR #384 follow-up finding 3: `data !== undefined` alone is also
  // true while a BACKGROUND refresh of this same query is in flight
  // (`refreshing`, per useQuery's own contract) — a cached filter must not
  // count as settled until that refresh itself resolves, or editing could
  // start from stale cached data a refresh is about to replace.
  const audienceFilterSettled =
    (audienceFilterQuery.data !== undefined || audienceFilterNotFound) &&
    !audienceFilterQuery.refreshing &&
    !audienceFilterQuery.stale &&
    !audienceFilterLoadError;
  // Codex finding 1 (PR #384): while this GET is still pending, or has
  // failed with anything other than the intentional 404 ("legitimately none
  // saved yet"), `savedFilter` below falls back to an EMPTY filter object —
  // opening/saving the Editar campaña modal against that fallback could
  // silently overwrite a real saved filter with empty values. `settled`
  // already excludes a genuine (non-404) error (data stays undefined and
  // `notFound` stays false), so this one flag covers both "still loading"
  // and "failed to load".
  const audienceFilterBlocked = !audienceFilterSettled;
  const savedFilter: AdminAudienceFilterSelection = audienceFilterQuery.data ?? {
    showId: null,
    paisEmpresa: null,
    empresa: null,
    empresas: null,
    cargo: null,
    categoria: null,
  };

  // Remaining Codex PR #384 follow-up finding 2: a legacy Segment campaign
  // (audience-filter GET 404, `audienceFilterNotFound`) has no saved filter
  // of its own -- running this preview there would query with an EMPTY
  // filter object (every field `undefined`), i.e. "match every contact",
  // producing a confidently-wrong ≈N for a campaign that is actually scoped
  // by its Segment. Never run the preview for that case; `audienceSummaryLabel`
  // (below) shows "Audiencia definida por el segmento" instead everywhere
  // this total would otherwise render.
  const previewQuery = useQuery({
    key: ["campaign-audience-preview", id, audienceCriteriaKey(savedFilter)],
    queryFn: () =>
      getCampaignAudiencePreview({
        showId: savedFilter.showId ?? undefined,
        paisEmpresa: savedFilter.paisEmpresa ?? undefined,
        empresas: savedFilter.empresas && savedFilter.empresas.length > 0 ? savedFilter.empresas : undefined,
        cargo: savedFilter.cargo ?? undefined,
        categoria: savedFilter.categoria ?? undefined,
        page: 1,
        size: 1,
      }),
    enabled: audienceFilterSettled && !audienceFilterNotFound,
  });
  const audienceTotal = previewQuery.data?.total;
  // Codex PR #384 follow-up finding 4: a failed preview request left `total`
  // `undefined` forever, which `audienceSummaryLabel`/`audienceCountLabel`
  // rendered as "calculando…" below -- indistinguishable from a slow-but-
  // working request, both on the page and in the send dialogs. Only
  // genuinely a failure when there is no data to fall back on (same
  // `stale`-aware convention `CampaignCreatePanel`'s own audience preview
  // already uses) -- a background refresh failing while a prior count is
  // still on screen keeps showing that last known count instead.
  const audiencePreviewFailed = previewQuery.data === undefined && previewQuery.error !== undefined;
  const audienceLabel = audiencePreviewFailed ? "—" : audienceSummaryLabel(audienceTotal, audienceFilterNotFound);

  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [dispatchPollAttempts, setDispatchPollAttempts] = useState(0);

  useEffect(() => {
    if (dispatchPollAttempts === 0) return undefined;
    if (!data || data.estado !== "programada" || dispatchPollAttempts > DISPATCH_POLL_MAX_ATTEMPTS) {
      setDispatchPollAttempts(0);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      refetchDetail();
      setDispatchPollAttempts((n) => n + 1);
    }, DISPATCH_POLL_INTERVAL_MS);
    return () => clearTimeout(timeoutId);
  }, [dispatchPollAttempts, data, refetchDetail]);

  const invalidateKeys = useMemo(
    () => [
      ["campaign", id],
      ["campaign-monitoring", id],
      ["campaign-telemetry", id],
      ["overview"],
    ] as const,
    [id],
  );

  const scheduleMutation = useMutation({
    mutationFn: (fecha: string) => scheduleCampaign(id, { fechaProgramada: fecha }),
    invalidateKeys,
  });
  const dispatchMutation = useMutation({
    mutationFn: () => dispatchCampaign(id),
    invalidateKeys,
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelCampaign(id),
    invalidateKeys,
  });
  const updateMutation = useMutation({
    mutationFn: (draft: AdminCampaignDraft) => updateCampaign(id, draft),
    invalidateKeys,
  });
  const filterMutation = useMutation({
    mutationFn: (selection: AdminAudienceFilterSelection) => saveAudienceFilter(id, selection),
    // Codex finding 8 (PR #384): the Audiencia card reads Show name etc.
    // from the CAMPAIGN DETAIL query, not this one — without also
    // invalidating it, saving the audience filter left that card showing
    // stale data until some unrelated refetch happened to run.
    invalidateKeys: [["campaign-audience-filter", id], ["campaign", id]],
  });

  if (isPending) {
    return (
      <>
        <h1>Campaña</h1>
        <p role="status">Cargando campaña…</p>
      </>
    );
  }

  if (isNotFound) {
    return (
      <NotFoundCard
        ariaLabel="Campaña no encontrada"
        title="Campaña no encontrada"
        description="La campaña solicitada no existe o no está disponible para esta cuenta."
        secondaryAction={{ label: "Volver a Campañas", to: `/campanas${contextSuffix}` }}
        primaryAction={{ label: "Ir al inicio", to: "/" }}
      />
    );
  }

  if (!data) {
    return (
      <FeedbackPanel
        variant="error"
        message={describeError(error, "No se pudo cargar la campaña.")}
        onRetry={refetchDetail}
      />
    );
  }

  const { label, tone } = resolveLifecycleStatus(data.estado);
  const { canEdit, canSchedule, canCancel, canDispatch } = lifecycleControls(data.estado);
  const hasMore = canEdit || canCancel;
  // Codex review round 4 (P1, PR #384): a batch-created draft has estado='borrador' like
  // any other draft, but its audience was already fixed at creation time -- the backend's
  // own `audiencia_fijada_en IS NULL` guard on the filter PUT
  // (AdminAudienceFilterSqlBuilder.BuildUpsertAudienceFilterCommand) always 409s for it, so
  // this must never attempt that PUT (nor let the operator edit the frozen fields).
  const audienceFixed = data.audienciaFijadaEn !== null;
  const statusLine = statusLineFor(data.estado, data.fechaProgramada, Date.now());

  const monitoring = monitoringQuery.data;
  const telemetry = telemetryQuery.data;
  const entregado = telemetry ? metricValue(telemetry.metrics, "entregado") : null;
  const pollActive = monitoring ? !isTerminalLifecycleState(monitoring.estado) && monitoring.estado === "enviando" : false;
  const pollTerminal = monitoring ? isTerminalLifecycleState(monitoring.estado) : false;
  const pollNote = pollActive ? "Actualizando en vivo" : pollTerminal ? "Envío finalizado" : "Sin actividad todavía";

  // Codex finding (PR #384 round 5): `AdminCampaignMonitoring`'s
  // `encolados`/`fallidos`/`suprimidos` are typed `number`, but that is only
  // a compile-time claim — the removed monitoring view validated these at
  // runtime and showed "Dato inválido" for exactly this reason (same
  // contract-mismatch risk `isValidCount`'s own docstring describes for a
  // JSON-parsed response). `?? 0` turned that unavailable data into a
  // confirmed, measured zero: an absent/invalid `encolados` additionally
  // selected the waiting branch below and claimed nothing had been sent,
  // when the true answer is simply unknown.
  const encoladosValid = !monitoring || isValidCount(monitoring.encolados);
  const fallidosValid = !monitoring || isValidCount(monitoring.fallidos);
  const suprimidosValid = !monitoring || isValidCount(monitoring.suprimidos);
  const encolados = monitoring?.encolados ?? 0;
  const fallidos = monitoring?.fallidos ?? 0;
  // progress-bar-sent: the bar follows the SENDING pace (ACS-accepted recipients). A
  // missing/invalid `enviados` (older API) is unavailable (null), never a fabricated 0.
  const enviados = monitoring && isValidCount(monitoring.enviados) ? (monitoring.enviados ?? null) : null;
  const paceCards = sendPaceCards({
    estado: monitoring?.estado ?? data.estado,
    enviados,
    primerEnvioEn: monitoring?.primerEnvioEn,
    ultimoEnvioEn: monitoring?.ultimoEnvioEn,
    nowMs: Date.now(),
  });
  const completedLine = completionLine(monitoring?.estado ?? data.estado, monitoring?.completadaEn);
  const pendientesConfirmacion = isValidCount(monitoring?.pendientesConfirmacion) ? (monitoring?.pendientesConfirmacion ?? 0) : 0;
  const sinConfirmacion = isValidCount(monitoring?.sinConfirmacion) ? (monitoring?.sinConfirmacion ?? 0) : 0;
  // `pendingCount` validates its own inputs and returns `null` for a
  // genuinely invalid one — pass the RAW monitoring fields (not the
  // defaulted-to-0 locals above) so it can actually catch this.
  const pendientes = monitoring ? pendingCount(monitoring.encolados, entregado, monitoring.fallidos) : null;

  // Codex finding (PR #384 round 5): after a schedule/dispatch/cancel/edit
  // request fails, closing and reopening the dialog passed the SAME mutation
  // error straight into the fresh modal, because these screen-scoped
  // `useMutation` instances stay mounted across the modal's open/close
  // cycle and had no way to clear their own result. Reset every
  // modal-triggering mutation's state whenever a modal closes (including the
  // close that immediately follows a successful submit) so a freshly opened
  // modal never inherits a previous attempt's error.
  const closeModal = () => {
    setActiveModal(null);
    scheduleMutation.reset();
    dispatchMutation.reset();
    cancelMutation.reset();
    updateMutation.reset();
    filterMutation.reset();
  };

  return (
    <section aria-label="Detalle de campaña" className="v2-campaign-detail">
      <p>
        <Link to={`/campanas${contextSuffix}`}>‹ Campañas</Link>
      </p>

      {stale && (
        <FeedbackPanel variant="stale" message="Datos anteriores de esta campaña." onRetry={refetchDetail} />
      )}

      <header className="v2-campaign-detail__header">
        {/* Panel Rediseño.dc.html línea 192-195: pill, H1, and the status
            sentence stack as their own lines (column), never inline on the
            same row — the prototype wins over the pre-redesign capture this
            replaces. */}
        <div className="v2-campaign-detail__header-main">
          <StatusPill label={label} tone={tone} />
          <h1>{data.nombre}</h1>
          <p className="v2-campaign-detail__status-line">{statusLine}</p>
        </div>

        <RequireRole role="Operador">
          {(canSchedule || canDispatch) && (
            <div className="v2-campaign-detail__header-actions">
              {canSchedule && (
                <button type="button" className="v2-u-target-min v2-btn-primary" onClick={() => setActiveModal("schedule")}>
                  Programar envío
                </button>
              )}
              {canDispatch && (
                <button type="button" className="v2-u-target-min v2-btn-primary" onClick={() => setActiveModal("dispatch")}>
                  Enviar ahora
                </button>
              )}
              {hasMore && (
                <MoreMenu
                  triggerRef={moreMenuTriggerRef}
                  canEdit={canEdit}
                  // Codex finding (PR #384 round 5): for a batch-created
                  // draft with `audienceFixed === true`, the modal disables
                  // all audience controls and `handleSubmit` deliberately
                  // skips `filterMutation` — content-only edits (nombre,
                  // asunto, template, fecha) never depend on this GET
                  // succeeding. Ignore `audienceFilterBlocked` at the
                  // trigger when the audience is already fixed, instead of
                  // blocking the whole Editar campaña action on it.
                  editBlocked={!audienceFixed && audienceFilterBlocked}
                  editBlockedError={audienceFilterLoadError}
                  editBlockedErrorMessage={describeError(
                    audienceFilterQuery.error,
                    "No se pudo cargar el filtro de audiencia.",
                  )}
                  onRetryEditBlocked={audienceFilterQuery.refetch}
                  onEdit={() => setActiveModal("edit")}
                  onCancel={() => setActiveModal("cancel")}
                />
              )}
            </div>
          )}
        </RequireRole>
        {confirmedConsulta && (
          <p className="v2-u-help-text">
            Las acciones de escritura requieren el rol Operador. El rol Consulta conserva todas las lecturas.
          </p>
        )}
      </header>

      <section aria-label="Progreso del envío" className="v2-campaign-progress">
        <div className="v2-campaign-progress__head">
          <h2>Progreso del envío</h2>
          <span className="v2-campaign-progress__poll">
            <span
              className={pollActive ? "v2-campaign-progress__poll-dot--active" : "v2-campaign-progress__poll-dot--terminal"}
              aria-hidden="true"
            />
            {pollNote}
          </span>
        </div>

        {/* Codex finding 9 (PR #384): a background monitoring refresh that
            fails AFTER an earlier successful response rendered nothing at
            all — the stats below kept showing (correctly, per `stale`'s own
            contract: keep previous data visible), but with no hint they
            might be out of date and no way to retry. Same small stale-note
            convention as the top-level `stale` banner above. */}
        {monitoring && monitoringQuery.stale && (
          <FeedbackPanel variant="stale" message="No se pudo actualizar." onRetry={monitoringQuery.refetch} />
        )}

        {!monitoring ? (
          monitoringQuery.error !== undefined ? (
            <FeedbackPanel
              variant="error"
              message={describeError(monitoringQuery.error, "No se pudo cargar el progreso del envío.")}
              onRetry={monitoringQuery.refetch}
            />
          ) : (
            <p role="status">Cargando progreso…</p>
          )
        ) : !encoladosValid ? (
          // Codex finding (PR #384 round 5): an invalid/missing `encolados`
          // is simply UNKNOWN, never a confirmed zero — it must not select
          // the "Todavía no se ha enviado" waiting box below, which asserts
          // nothing has been queued yet.
          <FeedbackPanel variant="error" message="Dato inválido en el progreso del envío." />
        ) : encolados === 0 ? (
          <div className="v2-campaign-progress__wait">
            <div>
              <strong>{waitTitleFor(data.estado, data.fechaProgramada)}</strong>
              <span>{waitTextFor(data.estado, audienceTotal, audienceFilterNotFound)}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="v2-campaign-progress__summary">
              <span className="v2-campaign-progress__pct v2-u-tabular-nums">
                {formatDeliveredPercent(enviados, encolados)}
              </span>
              <span>{formatSentHeadline(enviados, encolados)}</span>
            </div>
            <DeliveryProgressBar
              delivered={enviados ?? 0}
              errorCount={fallidos}
              total={encolados}
              height={12}
              countLabel="enviados"
              active={monitoring.estado === "enviando"}
              // Codex finding 7 (PR #384): never announce a fabricated "0 de
              // N enviados" to assistive tech when the figure itself is
              // unavailable — DeliveryProgressBar already supports hiding
              // itself for exactly this case.
              deliveredAvailable={enviados !== null}
            />
            {/* Codex finding 6 (PR #384): a telemetry request failure while
                monitoring already has queued messages otherwise degrades
                silently to "—"/"Sin medir" — indistinguishable from a
                genuinely unavailable metric, with no way to retry. The
                monitoring-sourced stats below (Errores/Excluidos/Pendientes)
                stay visible either way. */}
            {telemetryQuery.error !== undefined && (
              <FeedbackPanel
                variant="error"
                message={describeError(telemetryQuery.error, "No se pudo cargar el número de entregados.")}
                onRetry={telemetryQuery.refetch}
              />
            )}
            <div className="v2-campaign-progress__stats">
              <MiniStat
                label="Entregados"
                tone="delivered"
                value={formatOrDash(entregado)}
                hint={entregado === null ? "Sin medir" : `${formatDeliveredPercent(entregado, encolados)} del total`}
              />
              <MiniStat
                label="Errores"
                tone="error"
                // Codex finding (PR #384 round 5): render the shared
                // "Dato inválido" critical span for a missing/non-numeric
                // `fallidos` instead of `formatCount`'s defaulted-to-0 value.
                value={renderValidatedCount(monitoring.fallidos, formatCount)}
                hint={!fallidosValid ? "Dato no disponible" : fallidos > 0 ? "No se pudieron entregar" : "Sin errores"}
                critical={!fallidosValid || fallidos > 0}
              />
              <MiniStat
                label="Pendientes"
                tone="pending"
                value={formatOrDash(pendientes)}
                // Codex PR #384 follow-up finding 7: `pendingCount` returns
                // `null` for a genuinely UNAVAILABLE figure (its own
                // docstring: "never a fabricated figure") -- treating falsy
                // `null` the same as a confirmed `0` here used to claim
                // "Ninguno" ("there are definitely zero pending") when the
                // true answer is simply unknown. Same "Sin medir" convention
                // the Entregados card above already uses.
                hint={pendientes === null ? "Sin medir" : pendientes > 0 ? "En cola de envío" : "Ninguno"}
              />
              <MiniStat
                label="Excluidos"
                tone="excluded"
                value={renderValidatedCount(monitoring.suprimidos, formatCount)}
                hint={suprimidosValid ? "Contactos suprimidos" : "Dato no disponible"}
              />
            </div>
            {/* campaign-pace-metrics: how long the sending took and the average pace, from the
                first to the last email sent (live elapsed time while still sending). */}
            <div className="v2-campaign-progress__stats v2-campaign-progress__stats--pace">
              <MiniStat label="Tiempo de envío" tone="duration" value={paceCards.time.value} hint={paceCards.time.hint} />
              <MiniStat label="Ritmo promedio" tone="rate" value={paceCards.rate.value} hint={paceCards.rate.hint} />
            </div>
            {completedLine && <p className="v2-campaign-progress__note">{completedLine}</p>}
            {(pendientesConfirmacion > 0 || sinConfirmacion > 0) && (
              <p className="v2-campaign-progress__note">
                {pendientesConfirmacion > 0 && `Por confirmar: ${formatCount(pendientesConfirmacion)} (esperando reporte de entrega)`}
                {pendientesConfirmacion > 0 && sinConfirmacion > 0 && " · "}
                {sinConfirmacion > 0 && `Sin confirmación: ${formatCount(sinConfirmacion)} (sin reporte tras 48 h)`}
              </p>
            )}
          </>
        )}

        <p className="v2-campaign-progress__note">
          Aperturas, clics, rebotes y quejas de spam todavía no se miden; aparecerán aquí cuando estén disponibles.
        </p>
      </section>

      <div className="v2-campaign-detail__cards">
        <DetailGroup
          title="Contenido"
          items={[
            { label: "Asunto", value: data.asunto ?? "—" },
            { label: "Plantilla", value: data.templateName },
            { label: "Creada por", value: formatActor(data.createdBy) },
          ]}
          footer={
            <Link to={`/plantillas/${data.templateId}`} className="v2-campaign-detail__template-link">
              Ver plantilla →
            </Link>
          }
        />
        <DetailGroup
          title="Audiencia"
          items={[
            { label: "Show", value: data.showName ?? "Todos" },
            { label: "Empresas", value: formatEmpresas(resolveEmpresas(savedFilter)) },
            {
              label: "País · Cargo · Categoría",
              value: formatPaisCargoCategoria(savedFilter.paisEmpresa, savedFilter.cargo, savedFilter.categoria),
            },
            { label: "Contactos activos", value: audienceLabel },
          ]}
          footer={
            // Codex PR #384 follow-up finding 4: surface the failed preview
            // request with the same FeedbackPanel + Reintentar convention
            // used elsewhere on this screen, instead of leaving "Contactos
            // activos" stuck on a silent "—" with no way to retry.
            audiencePreviewFailed ? (
              <FeedbackPanel
                variant="error"
                message="No se pudo calcular la audiencia."
                onRetry={previewQuery.refetch}
              />
            ) : // Codex finding 3 (PR #384): a legacy draft (audience-filter GET
            // 404) still schedules via its Segment until a real filter is
            // saved — the card above otherwise reads identically to "no
            // restriction", which would misleadingly suggest THIS card
            // already governs the send.
            audienceFilterNotFound ? (
              <p className="v2-u-help-text">
                Esta audiencia todavía proviene del Segmento asignado a la campaña — no hay un filtro guardado.
              </p>
            ) : undefined
          }
        />
      </div>

      {activeModal === "schedule" && (
        <ScheduleModal
          campaignName={data.nombre}
          templateName={data.templateName}
          fechaProgramada={data.fechaProgramada}
          audienceLabel={audienceLabel}
          submitting={scheduleMutation.status === "pending"}
          error={scheduleMutation.error}
          onClose={closeModal}
          onSubmit={(fecha) =>
            scheduleMutation.mutate(fecha).then(
              () => {
                closeModal();
                showToast(`"${data.nombre}" quedó programada para ${formatDateTime(fecha)}`);
              },
              // `scheduleMutation.error` (rendered above) is the single
              // source of truth for the failure UI — same two-argument
              // `.then(onSuccess, onError)` convention as the pre-merge
              // CampaignSummaryTab.tsx, avoiding an unhandled rejection.
              () => {},
            )
          }
        />
      )}

      {activeModal === "dispatch" && (
        <DispatchModal
          campaignName={data.nombre}
          fechaProgramada={data.fechaProgramada}
          audienceLabel={audienceLabel}
          submitting={dispatchMutation.status === "pending"}
          error={dispatchMutation.error}
          onClose={closeModal}
          onSubmit={() =>
            dispatchMutation.mutate(undefined).then(
              () => {
                closeModal();
                refetchDetail();
                setDispatchPollAttempts(1);
                showToast("Envío iniciado");
              },
              () => {},
            )
          }
        />
      )}

      {activeModal === "cancel" && (
        <CancelModal
          campaignName={data.nombre}
          wasScheduled={data.estado === "programada"}
          fechaProgramada={data.fechaProgramada}
          restoreFocusTo={moreMenuTriggerRef.current}
          submitting={cancelMutation.status === "pending"}
          error={cancelMutation.error}
          onClose={closeModal}
          onSubmit={() =>
            cancelMutation.mutate(undefined).then(
              () => {
                closeModal();
                showToast("Campaña cancelada");
              },
              () => {},
            )
          }
        />
      )}

      {activeModal === "edit" && (
        <EditCampaignModal
          campaign={data}
          savedFilter={savedFilter}
          restoreFocusTo={moreMenuTriggerRef.current}
          // Codex finding 1 (PR #384): defense in depth — the "Editar
          // campaña" trigger above is already disabled while this is true,
          // but a background refetch could flip it to true WHILE the modal
          // is already open (e.g. this same query re-fetching and failing).
          audienceFilterBlocked={audienceFilterBlocked}
          audienceFixed={audienceFixed}
          updateSubmitting={updateMutation.status === "pending"}
          updateError={updateMutation.error}
          filterSubmitting={filterMutation.status === "pending"}
          filterError={filterMutation.error}
          onClose={closeModal}
          onSubmit={(draft, selection) =>
            updateMutation
              .mutate(draft)
              .then(() =>
                // Codex review round 4 (P1, PR #384): a batch-created draft's audience is
                // ALREADY fixed (audienciaFijadaEn set) even while estado is still
                // 'borrador' -- the backend's own guard always 409s a later filter PUT for
                // it, so this must never be attempted (the fields are disabled in the modal
                // too, so `selection` here is always just the unchanged `savedFilter`
                // anyway). Checked BEFORE the empty-selection short-circuit below so a
                // fixed-but-empty audience (e.g. a batch with no criterion) still skips the
                // PUT instead of falling through to it.
                audienceFixed
                  ? undefined
                  : // Codex finding 3 (PR #384): never PUT an all-empty audience
                    // filter just because the operator re-saved the form without
                    // setting any criterion — for a legacy draft that still has
                    // none saved, that silent PUT would create a real (empty)
                    // filter row that permanently replaces the Segment fallback.
                    //
                    // Codex PR #384 follow-up finding 1: that short-circuit must
                    // NOT also swallow a REAL clear — when a filter was already
                    // saved (`!audienceFilterNotFound`), the operator removing
                    // every criterion and saving is a deliberate "clear the
                    // filter" action; skipping the PUT there would leave the
                    // stale filter in place while still showing "Cambios
                    // guardados" as if it had been cleared. There is no separate
                    // delete/clear endpoint (`api.ts`) — PUTting the all-null
                    // selection through the existing contract IS the clear.
                    audienceFilterNotFound && isEmptyAudienceSelection(selection)
                    ? undefined
                    : filterMutation.mutate(selection),
              )
              .then(
                () => {
                  closeModal();
                  showToast("Cambios guardados");
                },
                // Both mutations already recorded their own `error` state
                // (rendered above, `updateError`/`filterError`) before
                // re-throwing — this branch only prevents an unhandled
                // rejection; the campaign update's own success is never
                // rolled back when the SUBSEQUENT filter save fails (§5.3:
                // "si falla el filtro, mostrar el error dentro del modal").
                () => {},
              )
          }
        />
      )}
    </section>
  );
}

function MoreMenu({
  triggerRef,
  canEdit,
  editBlocked,
  editBlockedError,
  editBlockedErrorMessage,
  onRetryEditBlocked,
  onEdit,
  onCancel,
}: {
  // Codex PR #384 follow-up finding 6: exposed so the parent can restore
  // focus HERE (not to whatever menu item was clicked, which unmounts in the
  // same batched update as the modal mounting) once a modal opened from this
  // menu closes — see `Modal`'s own `restoreFocusTo` prop.
  triggerRef: RefObject<HTMLButtonElement | null>;
  canEdit: boolean;
  // Codex finding 1 (PR #384): true while the audience-filter GET is still
  // pending, or failed with anything other than the intentional 404.
  editBlocked: boolean;
  editBlockedError: boolean;
  editBlockedErrorMessage: string;
  onRetryEditBlocked: () => void;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function closeIfOutside(event: MouseEvent) {
      if (wrapperRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      // Codex finding (PR #384 round 5): closing only unmounted the menu —
      // when a keyboard user had tabbed into one of its items, that item
      // (and the focus on it) went away in the same update, leaving focus
      // on `document.body`. Move it back to the trigger explicitly, the
      // same destination this menu's other close paths already restore to.
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="v2-campaign-detail__more" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className="v2-u-target-min v2-btn-secondary"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Más ▾
      </button>
      {open && (
        <div role="menu" className="v2-campaign-detail__more-menu">
          {canEdit && (
            <button
              type="button"
              role="menuitem"
              disabled={editBlocked}
              onClick={() => {
                // Defense in depth: the `disabled` attribute above already
                // stops a real pointer/keyboard activation from firing this
                // handler — this guard only protects a programmatic/forced
                // activation from opening the modal while blocked.
                if (editBlocked) return;
                setOpen(false);
                onEdit();
              }}
            >
              Editar campaña
            </button>
          )}
          {canEdit && editBlockedError && (
            <p role="alert" className="v2-campaign-detail__more-menu-error">
              {editBlockedErrorMessage}{" "}
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={onRetryEditBlocked}>
                Reintentar
              </button>
            </p>
          )}
          <button
            type="button"
            role="menuitem"
            className="v2-u-danger"
            onClick={() => {
              setOpen(false);
              onCancel();
            }}
          >
            Cancelar campaña
          </button>
        </div>
      )}
    </div>
  );
}

function ScheduleModal({
  campaignName,
  templateName,
  fechaProgramada,
  audienceLabel,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  campaignName: string;
  templateName: string;
  fechaProgramada: string;
  audienceLabel: string;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (fecha: string) => Promise<unknown>;
}) {
  const [fecha, setFecha] = useState(fechaProgramada);
  const [touched, setTouched] = useState(false);
  const validationError = validateScheduleDate(fecha);
  // Codex PR #384 follow-up finding 5: the shared `Modal` primitive closes
  // unconditionally on Escape -- same "never let Escape bypass a disabled
  // Cancelar button" guard `CampaignCreatePanel`'s own `requestClose` already
  // uses for its create panel.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Modal ariaLabel="Programar envío" onClose={requestClose}>
      <div className="v2-modal-v2 v2-modal-v2--w520">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>Programar envío</h2>
          <ModalCloseButton onClose={requestClose} disabled={submitting} />
        </header>
        <div className="v2-modal-v2__body">
          <p className="v2-modal-v2__intro">
            La campaña saldrá automáticamente en la fecha que elijas. Podrás cancelarla antes de esa hora.
          </p>
          <Field label="Fecha y hora de envío" htmlFor="schedule-modal-fecha">
            <input
              id="schedule-modal-fecha"
              type="datetime-local"
              value={isoToDatetimeLocalValue(fecha)}
              onChange={(event) => {
                setFecha(datetimeLocalValueToIso(event.target.value));
                setTouched(true);
              }}
              disabled={submitting}
              aria-invalid={touched && validationError ? true : undefined}
              aria-describedby={touched && validationError ? "schedule-modal-fecha-error" : undefined}
            />
          </Field>
          {touched && validationError && (
            <p id="schedule-modal-fecha-error" role="alert">
              {validationError}
            </p>
          )}
          <dl className="v2-modal-v2__details">
            <div>
              <dt>Campaña</dt>
              <dd>{campaignName}</dd>
            </div>
            <div>
              <dt>Plantilla</dt>
              <dd>{templateName}</dd>
            </div>
            <div>
              <dt>Destinatarios</dt>
              <dd>{audienceLabel}</dd>
            </div>
          </dl>
          {error !== undefined && <p role="alert">{describeConsequentialActionError(error)}</p>}
        </div>
        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={requestClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-btn-primary"
            disabled={submitting}
            aria-busy={submitting}
            onClick={() => {
              setTouched(true);
              if (validateScheduleDate(fecha)) return;
              onSubmit(fecha);
            }}
          >
            Programar envío
          </button>
        </footer>
      </div>
    </Modal>
  );
}

function DispatchModal({
  campaignName,
  fechaProgramada,
  audienceLabel,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  campaignName: string;
  fechaProgramada: string;
  audienceLabel: string;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: () => Promise<unknown>;
}) {
  // Codex PR #384 follow-up finding 5: same Escape-while-submitting guard as
  // `ScheduleModal` above.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Modal ariaLabel="¿Enviar ahora?" onClose={requestClose}>
      <div className="v2-modal-v2 v2-modal-v2--w500">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>¿Enviar ahora?</h2>
          <ModalCloseButton onClose={requestClose} disabled={submitting} />
        </header>
        <div className="v2-modal-v2__body">
          <p className="v2-modal-v2__hint--lead">El envío empieza de inmediato y no se puede detener desde aquí.</p>
          <dl className="v2-campaign-create__summary">
            <div>
              <dt>Campaña</dt>
              <dd>{campaignName}</dd>
            </div>
            <div>
              <dt>Destinatarios</dt>
              <dd>{audienceLabel}</dd>
            </div>
            <div>
              <dt>Estaba programada para</dt>
              <dd>{formatDateTime(fechaProgramada)}</dd>
            </div>
          </dl>
          {error !== undefined && <p role="alert">{describeConsequentialActionError(error)}</p>}
        </div>
        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={requestClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-btn-primary"
            disabled={submitting}
            aria-busy={submitting}
            onClick={() => onSubmit()}
          >
            Enviar ahora
          </button>
        </footer>
      </div>
    </Modal>
  );
}

function CancelModal({
  campaignName,
  wasScheduled,
  fechaProgramada,
  restoreFocusTo,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  campaignName: string;
  wasScheduled: boolean;
  fechaProgramada: string;
  restoreFocusTo?: HTMLElement | null;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: () => Promise<unknown>;
}) {
  // Codex PR #384 follow-up finding 5: same Escape-while-submitting guard as
  // `ScheduleModal`/`DispatchModal` above.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Modal ariaLabel="¿Cancelar esta campaña?" onClose={requestClose} restoreFocusTo={restoreFocusTo}>
      <div className="v2-modal-v2 v2-modal-v2--w500">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>¿Cancelar esta campaña?</h2>
          <ModalCloseButton onClose={requestClose} disabled={submitting} />
        </header>
        <div className="v2-modal-v2__body">
          <p className="v2-modal-v2__hint--lead">
            &quot;{campaignName}&quot; no se enviará. Esta acción no se puede deshacer.
          </p>
          {wasScheduled && (
            <p role="alert" className="v2-u-danger">
              Estaba programada para {formatDateTime(fechaProgramada)}.
            </p>
          )}
          {error !== undefined && <p role="alert">{describeConsequentialActionError(error)}</p>}
        </div>
        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={requestClose} disabled={submitting}>
            Volver
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-u-danger"
            disabled={submitting}
            aria-busy={submitting}
            onClick={() => onSubmit()}
          >
            Sí, cancelar campaña
          </button>
        </footer>
      </div>
    </Modal>
  );
}

interface EditForm {
  nombre: string;
  asunto: string;
  templateId: string;
  fechaProgramada: string;
  showId: string;
  empresas: string[];
  paisEmpresa: string;
  cargo: string;
  categoria: string;
}

function EditCampaignModal({
  campaign,
  savedFilter,
  restoreFocusTo,
  audienceFilterBlocked,
  audienceFixed,
  updateSubmitting,
  updateError,
  filterSubmitting,
  filterError,
  onClose,
  onSubmit,
}: {
  campaign: { campaignId: string; nombre: string; asunto: string | null; templateId: string; segmentId: string; fechaProgramada: string };
  savedFilter: AdminAudienceFilterSelection;
  restoreFocusTo?: HTMLElement | null;
  // Codex finding 1 (PR #384): true while the audience-filter GET is still
  // pending, or failed with anything other than the intentional 404 — saving
  // in that state could silently overwrite a real saved filter with the
  // empty fallback `savedFilter` above already degrades to.
  audienceFilterBlocked: boolean;
  // Codex review round 4 (P1, PR #384): true for a batch-created draft whose audience was
  // already fixed at creation time -- the backend always 409s a later filter PUT for it, so
  // the Audiencia fields below are disabled (nothing to save) and `onSubmit` never attempts
  // that PUT.
  audienceFixed: boolean;
  updateSubmitting: boolean;
  updateError: unknown;
  filterSubmitting: boolean;
  filterError: unknown;
  onClose: () => void;
  onSubmit: (draft: AdminCampaignDraft, selection: AdminAudienceFilterSelection) => Promise<unknown>;
}) {
  const [form, setForm] = useState<EditForm>({
    nombre: campaign.nombre,
    asunto: campaign.asunto ?? "",
    templateId: campaign.templateId,
    fechaProgramada: campaign.fechaProgramada,
    showId: savedFilter.showId ?? "",
    // Codex finding 2 (PR #384): hydrates a legacy scalar `empresa` (predates
    // the Empresa multi-select) as one pre-selected chip when `empresas`
    // itself is null — see `resolveEmpresas`'s own docstring.
    empresas: resolveEmpresas(savedFilter),
    paisEmpresa: savedFilter.paisEmpresa ?? "",
    cargo: savedFilter.cargo ?? "",
    categoria: savedFilter.categoria ?? "",
  });
  const [empresaValidation, setEmpresaValidation] = useState<"valid" | "pending" | "error">("valid");
  // Codex finding 4 (PR #384): "Guardar cambios" is type="button" OUTSIDE a
  // <form> (nesting one inside AudienceFilterEditor's own would be invalid
  // HTML — see that component's own docstring), so the Nombre input's
  // `required` attribute is never actually enforced by the browser.
  // Validated explicitly here instead, same inline-error convention as
  // `ScheduleModal`'s date field.
  const [validationError, setValidationError] = useState<string | null>(null);
  const submitting = updateSubmitting || filterSubmitting;
  // Codex review round 4 (P1, PR #384): the Audiencia fields are disabled when
  // `audienceFixed`, so `empresaValidation`/`audienceFilterBlocked` can never legitimately
  // block submit in that state either -- content-only edits (nombre/asunto/plantilla/fecha)
  // must stay possible for a fixed-audience draft.
  const submitBlocked = submitting || (!audienceFixed && (empresaValidation !== "valid" || audienceFilterBlocked));

  const handleSubmit = () => {
    if (submitBlocked) return;
    const trimmedNombre = form.nombre.trim();
    if (trimmedNombre === "" || form.templateId === "") {
      setValidationError("Completa el nombre y la plantilla antes de guardar.");
      return;
    }
    setValidationError(null);
    const draft: AdminCampaignDraft = {
      nombre: trimmedNombre,
      asunto: form.asunto.trim() === "" ? null : form.asunto,
      templateId: form.templateId,
      segmentId: campaign.segmentId,
      fechaProgramada: form.fechaProgramada,
    };
    const selection: AdminAudienceFilterSelection = {
      showId: form.showId === "" ? null : form.showId,
      paisEmpresa: form.paisEmpresa.trim() === "" ? null : form.paisEmpresa.trim(),
      empresa: null,
      empresas: form.empresas.length === 0 ? null : form.empresas,
      cargo: form.cargo.trim() === "" ? null : form.cargo.trim(),
      categoria: form.categoria.trim() === "" ? null : form.categoria.trim(),
    };
    onSubmit(draft, selection);
  };

  // Codex PR #384 follow-up finding 5: same Escape-while-submitting guard as
  // the other action modals above.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Modal ariaLabel="Editar campaña" onClose={requestClose} restoreFocusTo={restoreFocusTo}>
      <div className="v2-modal-v2 v2-modal-v2--w560">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>Editar campaña</h2>
          <ModalCloseButton onClose={requestClose} disabled={submitting} />
        </header>
        <div className="v2-modal-v2__body">
          <fieldset className="v2-modal-v2__group">
            <legend>Contenido</legend>
            <Field label="Nombre" htmlFor="edit-modal-nombre">
              <input
                id="edit-modal-nombre"
                value={form.nombre}
                onChange={(event) => setForm({ ...form, nombre: event.target.value })}
                required
                disabled={submitting}
              />
            </Field>
            <Field label="Asunto" htmlFor="edit-modal-asunto">
              <input
                id="edit-modal-asunto"
                value={form.asunto}
                onChange={(event) => setForm({ ...form, asunto: event.target.value })}
                disabled={submitting}
              />
            </Field>
            <TemplateSelector
              id="edit-modal-template"
              value={form.templateId}
              onChange={(templateId) => setForm({ ...form, templateId })}
              disabled={submitting}
            />
            <Field label="Fecha de envío" htmlFor="edit-modal-fecha">
              <input
                id="edit-modal-fecha"
                type="datetime-local"
                value={isoToDatetimeLocalValue(form.fechaProgramada)}
                onChange={(event) =>
                  setForm({ ...form, fechaProgramada: datetimeLocalValueToIso(event.target.value) })
                }
                disabled={submitting}
              />
            </Field>
          </fieldset>

          <fieldset className="v2-modal-v2__group" disabled={audienceFixed}>
            <legend>Audiencia</legend>
            {audienceFixed && (
              <p role="status">
                La audiencia de esta campaña ya quedó fijada al crearla y no se puede modificar.
              </p>
            )}
            <div className="v2-audience-grid">
              <ShowSelector
                id="edit-modal-show"
                value={form.showId}
                onChange={(showId) => setForm({ ...form, showId })}
                disabled={submitting || audienceFixed}
                allowEmpty
              />
              <EmpresaSelector
                id="edit-modal-empresa"
                value={form.empresas}
                onChange={(empresas) => setForm({ ...form, empresas })}
                disabled={submitting || audienceFixed}
                showId={form.showId || undefined}
                onValidationChange={setEmpresaValidation}
                hideChips
              />
              <Field label="País de la empresa" htmlFor="edit-modal-pais">
                <input
                  id="edit-modal-pais"
                  value={form.paisEmpresa}
                  onChange={(event) => setForm({ ...form, paisEmpresa: event.target.value })}
                  disabled={submitting || audienceFixed}
                />
              </Field>
              <Field label="Cargo" htmlFor="edit-modal-cargo">
                <input
                  id="edit-modal-cargo"
                  value={form.cargo}
                  onChange={(event) => setForm({ ...form, cargo: event.target.value })}
                  disabled={submitting || audienceFixed}
                />
              </Field>
              <Field label="Categoría" htmlFor="edit-modal-categoria">
                <input
                  id="edit-modal-categoria"
                  value={form.categoria}
                  onChange={(event) => setForm({ ...form, categoria: event.target.value })}
                  disabled={submitting || audienceFixed}
                />
              </Field>
            </div>
            <EmpresaChips
              value={form.empresas}
              onChange={(empresas) => setForm({ ...form, empresas })}
              disabled={submitting || audienceFixed}
            />
          </fieldset>

          {!audienceFixed && audienceFilterBlocked && (
            <p role="alert">
              No se pudo confirmar el filtro de audiencia guardado de esta campaña — guardar ahora podría
              sobrescribirlo con valores vacíos. Cierra e intenta de nuevo.
            </p>
          )}
          {validationError !== null && <p role="alert">{validationError}</p>}
          {updateError !== undefined && (
            <p role="alert">{describeError(updateError, "No se pudo guardar la campaña.")}</p>
          )}
          {filterError !== undefined && (
            <p role="alert">{describeError(filterError, "No se pudo guardar el filtro de audiencia.")}</p>
          )}
        </div>
        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={requestClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-btn-primary"
            disabled={submitBlocked}
            aria-busy={submitting}
            onClick={handleSubmit}
          >
            Guardar cambios
          </button>
        </footer>
      </div>
    </Modal>
  );
}
